"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/store/auth.store";
import { useToastStore } from "@/store/toast.store";
import { useAddressStore } from "@/store/address.store";
import { useAddressAutocomplete, useCreateAddress, useUpdateAddress, useAddresses, useDeleteAddress } from "@/hooks/use-addresses";
import { useGeolocation } from "@/hooks/use-geolocation";
import { authService } from "@/services/auth.service";
import { addressService } from "@/services/address.service";
import { guestStoreService } from "@/services/store.service";
import { useTranslation } from "@/lib/i18n";
import type { AddressAutocompleteResult, AddressType, CustomerAddress } from "@/types";
import { LoaderCircle, MapPin, Navigation, Pen, X } from "lucide-react";
import MapPreview, { Marker as MapPreviewMarker } from "react-map-gl/maplibre";
import { useMapStyle } from "@/lib/map/style";
import { MAP_MAX_BOUNDS } from "@/lib/map/provider";
import { useMapAutoResize } from "@/lib/map/use-map-autoresize";
import { refreshForLocationChange } from "@/lib/location-refresh";
import { checkCoverage } from "@/lib/coverage";
import "maplibre-gl/dist/maplibre-gl.css";
import "@/lib/map/worker";


type AddressStep = "search" | "detail" | "adjust-pin";

/** Matches the mobile app's address types. */
const ADDRESS_TYPES: { value: AddressType; labelKey: "typeHome" | "typeWork" | "typeOther" }[] = [
  { value: "home", labelKey: "typeHome" },
  { value: "work", labelKey: "typeWork" },
  { value: "other", labelKey: "typeOther" },
];

/**
 * The address picker — the single implementation behind both the navbar
 * dropdown and the in-cart address section, so the two can never drift.
 *
 * `guardStoreUlid` turns on delivery-zone validation: before an address is
 * applied we probe the geo-filtered store endpoint, which 400s when the
 * coordinates fall outside that store's delivery area. Used during checkout so
 * a customer can't pick an address the store can't deliver to.
 */
export function AddressPicker({
  onDone,
  guardStoreUlid,
}: {
  onDone?: () => void;
  guardStoreUlid?: string;
}) {
  const t = useTranslation();
  // Style Fast (jour / nuit, FR / AR) — même carte que l'app client.
  const mapStyle = useMapStyle();
  // Les deux aperçus vivent dans une feuille qui s'ouvre en glissant.
  const preview = useMapAutoResize();
  const dragMap = useMapAutoResize();
  const { user } = useAuthStore();
  const toast = useToastStore();
  const queryClient = useQueryClient();
  const { selectedAddressUlid, setSelectedAddressUlid, setGuestAddress } = useAddressStore();

  const [addressQuery, setAddressQuery] = useState("");
  const [debouncedAddressQuery, setDebouncedAddressQuery] = useState("");
  const [addressStep, setAddressStep] = useState<AddressStep>("search");
  const [editingUlid, setEditingUlid] = useState<string | null>(null);
  const [pickedResult, setPickedResult] = useState<AddressAutocompleteResult | null>(null);
  const [name, setName] = useState("");
  const [house, setHouse] = useState("");
  const [landmark, setLandmark] = useState("");
  const [addressType, setAddressType] = useState<AddressType>("home");
  const [zoneError, setZoneError] = useState<string | null>(null);
  const [checkingZone, setCheckingZone] = useState(false);
  // Live centre of the map while adjusting the pin (committed on save).
  const [pinCoords, setPinCoords] = useState<{ latitude: number; longitude: number } | null>(null);

  // Debounce address autocomplete query
  useEffect(() => {
    const id = setTimeout(() => setDebouncedAddressQuery(addressQuery), 300);
    return () => clearTimeout(id);
  }, [addressQuery]);

  const { coords, latitude: gpsLatitude, longitude: gpsLongitude } = useGeolocation();
  const { data: autocompleteRaw, isFetching } = useAddressAutocomplete({
    query: debouncedAddressQuery,
    latitude: coords?.latitude,
    longitude: coords?.longitude,
    limit: 5,
  });
  // Normalise: the API may return a plain array or a paginated { data: [] } shape
  const autocompleteData = (autocompleteRaw as { data?: AddressAutocompleteResult[] } | undefined)?.data;
  const autocompleteResults: AddressAutocompleteResult[] | null = Array.isArray(autocompleteRaw)
    ? autocompleteRaw
    : Array.isArray(autocompleteData)
    ? autocompleteData
    : null;
  const { data: savedAddresses } = useAddresses();
  const lastSyncedLocRef = useRef<string>("");
  const createAddress = useCreateAddress();
  const updateAddress = useUpdateAddress();
  const deleteAddress = useDeleteAddress();

  /** True when the guarded store delivers to these coordinates. */
  /**
   * Deux vérifications, et la première manquait.
   *
   * 1. Livre-t-on à cette adresse ? Le contrôle n'existait que pour protéger
   *    une commande en cours (`guardStoreUlid`) : depuis l'en-tête ou la
   *    fenêtre de localisation, n'importe quel point était accepté, et le
   *    client se retrouvait devant un catalogue vide sans explication.
   * 2. Le magasin du panier dessert-il cette adresse ? Inchangé.
   */
  const passesZone = useCallback(
    async (lat: number, lng: number) => {
      setZoneError(null);
      setCheckingZone(true);
      try {
        const coverage = await checkCoverage(lat, lng);
        if (coverage === "uncovered") {
          setZoneError(
            "Nous ne livrons pas encore à cette adresse. Choisissez-en une autre.",
          );
          return false;
        }

        if (!guardStoreUlid) return true;
        try {
          await guestStoreService.getById(guardStoreUlid, {
            latitude: lat,
            longitude: lng,
            company_ulid: process.env.NEXT_PUBLIC_COMPANY_ULID ?? "",
          });
          return true;
        } catch {
          setZoneError(t.cart.storeNotInZone);
          return false;
        }
      } finally {
        setCheckingZone(false);
      }
    },
    [guardStoreUlid, t]
  );

  const refreshForAddress = useCallback(
    async (lat: number, lng: number) => {
      if (user) {
        try {
          await authService.updateLocation({ latitude: lat, longitude: lng });
        } catch {
          // Synchronisation au mieux : on rafraîchit quand même, sinon un
          // échec réseau laisserait l'écran sur l'ancienne zone.
        }
      }
      refreshForLocationChange(queryClient, !!user);
    },
    [user, queryClient]
  );

  const handlePickSuggestion = async (result: AddressAutocompleteResult) => {
    if (!(await passesZone(result.latitude, result.longitude))) return;
    if (!user) {
      // Guest: persist reactively, refresh stores, close — no API call needed
      setGuestAddress({
        latitude: result.latitude,
        longitude: result.longitude,
        formatted: result.formatted,
        origin: "picked",
      });
      refreshForAddress(result.latitude, result.longitude);
      toast.show(t.header.addressChanged(result.formatted.split(",")[0]));
      onDone?.();
      return;
    }
    setPickedResult(result);
    setEditingUlid(null);
    setName("");
    setHouse("");
    setLandmark("");
    setAddressType("home");
    setAddressStep("detail");
  };

  const handleEditSaved = (e: React.MouseEvent, addr: CustomerAddress) => {
    e.stopPropagation();
    setPickedResult({
      place_id: addr.ulid,
      formatted: addr.formatted,
      latitude: addr.latitude,
      longitude: addr.longitude,
      country: null,
      city: null,
      postcode: null,
    });
    setEditingUlid(addr.ulid);
    setName(addr.name ?? "");
    setHouse(addr.house ?? "");
    setLandmark(addr.landmark ?? "");
    setAddressType(addr.type ?? "home");
    setAddressStep("detail");
  };

  const handleSave = async () => {
    if (!pickedResult) return;
    const isSaving = editingUlid ? updateAddress.isPending : createAddress.isPending;
    if (isSaving) return;
    if (!(await passesZone(pickedResult.latitude, pickedResult.longitude))) return;
    // Apply immediately as the active address (drives label + store list + sync).
    setGuestAddress({ latitude: pickedResult.latitude, longitude: pickedResult.longitude, formatted: pickedResult.formatted, origin: "picked" });
    lastSyncedLocRef.current = "";
    try {
      if (editingUlid) {
        await updateAddress.mutateAsync({
          ulid: editingUlid,
          payload: {
            formatted: pickedResult.formatted,
            latitude: pickedResult.latitude,
            longitude: pickedResult.longitude,
            name: name.trim() || undefined,
            house: house.trim() || undefined,
            landmark: landmark.trim() || undefined,
            type: addressType,
          },
        });
        setSelectedAddressUlid(editingUlid);
        refreshForAddress(pickedResult.latitude, pickedResult.longitude);
        toast.show(t.header.addressChanged(pickedResult.formatted.split(",")[0]));
      } else {
        const created = await createAddress.mutateAsync({
          formatted: pickedResult.formatted,
          latitude: pickedResult.latitude,
          longitude: pickedResult.longitude,
          name: name.trim() || undefined,
          house: house.trim() || undefined,
          landmark: landmark.trim() || undefined,
          type: addressType,
        });
        setSelectedAddressUlid(created.ulid);
        refreshForAddress(pickedResult.latitude, pickedResult.longitude);
        toast.show(t.header.addressChanged(pickedResult.formatted.split(",")[0]));
      }
    } catch {
      // ignore
    }
    setEditingUlid(null);
    onDone?.();
  };

  const handleSelectSaved = async (addr: CustomerAddress) => {
    if (!(await passesZone(addr.latitude, addr.longitude))) return;
    setSelectedAddressUlid(addr.ulid);
    setGuestAddress({ latitude: addr.latitude, longitude: addr.longitude, formatted: addr.name ?? addr.formatted, origin: "picked" });
    refreshForAddress(addr.latitude, addr.longitude);
    toast.show(t.header.addressChanged(addr.name ?? addr.formatted.split(",")[0]));
    onDone?.();
  };

  // Use the browser's current GPS position (reverse-geocoded to a real address).
  const handleUseCurrentLocation = async () => {
    const lat = gpsLatitude ?? coords?.latitude;
    const lng = gpsLongitude ?? coords?.longitude;
    if (lat == null || lng == null) return;
    if (!(await passesZone(lat, lng))) return;
    const reversed = await addressService.photonReverse(lat, lng).catch(() => null);
    const formatted = reversed?.formatted ?? t.header.currentLocation;
    setGuestAddress({ latitude: lat, longitude: lng, formatted, origin: "picked" });
    if (user) {
      try {
        const created = await createAddress.mutateAsync({ formatted, latitude: lat, longitude: lng });
        setSelectedAddressUlid(created.ulid);
      } catch { /* ignore — the active address above still drives everything */ }
    }
    lastSyncedLocRef.current = "";
    refreshForAddress(lat, lng);
    toast.show(t.header.addressChanged(formatted.split(",")[0]));
    onDone?.();
  };

  const handleDeleteAddress = (e: React.MouseEvent, ulid: string) => {
    e.stopPropagation();
    deleteAddress.mutate(ulid, {
      onSuccess: () => {
        if (selectedAddressUlid === ulid) {
          setSelectedAddressUlid(null);
        }
      },
    });
  };

  const handleCancel = () => {
    setAddressStep("search");
    setPickedResult(null);
    setEditingUlid(null);
  };

  return (
    <>
      {/* Delivery-zone rejection (checkout only) */}
      {zoneError && (
        <div className="border-b border-red-100 dark:border-red-900/40 bg-red-50 dark:bg-red-950/30 px-4 py-2.5">
          <p className="text-xs font-semibold text-red-600">{zoneError}</p>
        </div>
      )}

      {/* ── STEP: search ── */}
      {addressStep === "search" && (
        <>
          <div className="px-4 pt-4 pb-3">
            <p className="text-base font-semibold dark:text-gray-300 text-gray-900 mb-3">{t.header.enterAddress}</p>
            <div className="flex items-center gap-2 rounded-lg border border-gray-300 dark:border-[#313131] px-3 py-2.5 focus-within:border-black dark:focus-within:border-gray-500 transition">
              <MapPin className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                value={addressQuery}
                onChange={(e) => setAddressQuery(e.target.value)}
                placeholder={t.header.searchAddress}
                className="flex-1 text-sm bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none"
                data-address-search
              />
              {(isFetching || checkingZone) && (
                <LoaderCircle className="h-4 w-4 animate-spin text-gray-400 dark:text-gray-500 shrink-0" />
              )}
            </div>
          </div>

          {/* Use current browser location */}
          <button
            onClick={handleUseCurrentLocation}
            disabled={checkingZone}
            className="flex w-full items-center gap-3 border-t border-gray-100 dark:border-[#313131] px-4 py-3 text-left transition hover:bg-gray-50 dark:hover:bg-[#313131] disabled:opacity-60"
          >
            <Navigation className="h-4 w-4 shrink-0 text-secondary dark:text-primary" />
            <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{t.header.useCurrentLocation}</span>
          </button>

          {/* Autocomplete suggestions */}
          {autocompleteResults && autocompleteResults.length > 0 && (
            <div className="border-t border-gray-100 dark:border-[#313131]">
              {autocompleteResults.map((r) => (
                <button
                  key={r.place_id}
                  onClick={() => handlePickSuggestion(r)}
                  disabled={checkingZone}
                  className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-[#313131] transition disabled:opacity-60"
                >
                  <MapPin className="mt-0.5 h-4 w-4 text-gray-400 shrink-0" />
                  <span className="text-sm text-gray-800 dark:text-gray-200 leading-snug">{r.formatted}</span>
                </button>
              ))}
            </div>
          )}

          {/* Saved addresses */}
          {!autocompleteResults?.length && savedAddresses && savedAddresses.length > 0 && (
            <div className="border-t border-gray-100 dark:border-[#313131]">
              {savedAddresses.map((addr) => {
                const isActive = addr.ulid === (selectedAddressUlid ?? savedAddresses[0]?.ulid);
                return (
                  <div
                    key={addr.ulid}
                    className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-[#313131] transition cursor-pointer ${checkingZone ? "opacity-60" : ""}`}
                    onClick={() => handleSelectSaved(addr)}
                  >
                    <div className={`h-4 w-4 rounded-full border-2 shrink-0 flex items-center justify-center ${
                      isActive ? "border-primary" : "border-gray-400"
                    }`}>
                      {isActive && <div className="h-2 w-2 rounded-full bg-primary" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold truncate ${
                        isActive ? "text-primary" : "text-gray-800 dark:text-gray-200"
                      }`}>
                        {addr.name ?? addr.formatted.split(",")[0]}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{addr.formatted}</p>
                    </div>
                    <button
                      onClick={(e) => handleEditSaved(e, addr)}
                      className="text-gray-400 hover:text-gray-700 transition p-1 shrink-0"
                    >
                      <Pen className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => handleDeleteAddress(e, addr.ulid)}
                      disabled={deleteAddress.isPending}
                      className="text-gray-400 hover:text-red-500 transition p-1 shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add new address */}
          <div className="border-t border-gray-100 dark:border-[#313131]">
            <button
              onClick={() => {
                setPickedResult(null);
                setEditingUlid(null);
                setAddressQuery("");
                setAddressStep("search");
                setTimeout(() => {
                  const el = document.querySelector<HTMLInputElement>("[data-address-search]");
                  el?.focus();
                }, 0);
              }}
              className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-[#313131] transition"
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-gray-500 text-gray-500 shrink-0 text-base leading-none">+</span>
              <div>
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{t.header.addAddress}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t.header.addAddressSub}</p>
              </div>
            </button>
          </div>
        </>
      )}

      {/* ── STEP: detail ── */}
      {addressStep === "detail" && (
        <>
          {/* Map preview of the picked spot */}
          <div ref={preview.containerRef} className="relative h-36 overflow-hidden bg-[#e8ecef]">
            {pickedResult && mapStyle ? (
              <MapPreview
                trackResize={false} /* observateur interne : cf. useMapAutoResize */
                key={`${pickedResult.latitude},${pickedResult.longitude}`}
                initialViewState={{ latitude: pickedResult.latitude, longitude: pickedResult.longitude, zoom: 15 }}
                style={{ width: "100%", height: "100%" }}
                mapStyle={mapStyle}
                interactive={false}
                attributionControl={false}
                onLoad={preview.onLoad}
              >
                <MapPreviewMarker latitude={pickedResult.latitude} longitude={pickedResult.longitude} anchor="bottom">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-900 shadow-lg ring-2 ring-white">
                    <MapPin className="h-4 w-4 text-white" />
                  </div>
                </MapPreviewMarker>
              </MapPreview>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <MapPin className="h-10 w-10 text-gray-400" />
              </div>
            )}
          </div>

          <div className="flex items-start justify-between px-4 pt-3 pb-2">
            <div className="min-w-0 flex-1 mr-3">
              <p className="text-base font-semibold text-gray-900 dark:text-white truncate">
                {pickedResult ? pickedResult.formatted.split(",")[0] : t.header.newAddress}
              </p>
              {pickedResult && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{pickedResult.formatted}</p>
              )}
            </div>
            <button
              onClick={() => setAddressStep("adjust-pin")}
              className="shrink-0 text-sm font-semibold text-gray-800 dark:text-gray-200 underline underline-offset-2 hover:text-black dark:hover:text-white transition"
            >
              {t.header.adjust}
            </button>
          </div>

          <div className="px-4 pb-4 space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                {t.header.addressName}
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={pickedResult?.formatted.split(",")[0] ?? t.header.addressName}
                className="w-full rounded-lg border border-gray-200 dark:border-[#313131] bg-transparent text-gray-900 dark:text-gray-100 px-3 py-2.5 text-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-black dark:focus:border-gray-500 transition"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                {t.header.apartmentOrSuite}
              </label>
              <input
                type="text"
                value={house}
                onChange={(e) => setHouse(e.target.value)}
                placeholder={t.header.apartmentOrSuite}
                className="w-full rounded-lg border border-gray-200 dark:border-[#313131] bg-transparent text-gray-900 dark:text-gray-100 px-3 py-2.5 text-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-black dark:focus:border-gray-500 transition"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                {t.header.landmark}
              </label>
              <input
                type="text"
                value={landmark}
                onChange={(e) => setLandmark(e.target.value)}
                placeholder={t.header.landmark}
                className="w-full rounded-lg border border-gray-200 dark:border-[#313131] bg-transparent text-gray-900 dark:text-gray-100 px-3 py-2.5 text-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-black dark:focus:border-gray-500 transition"
              />
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">{t.header.addressType}</p>
              <div className="grid grid-cols-3 gap-2">
                {ADDRESS_TYPES.map(({ value, labelKey }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setAddressType(value)}
                    className={`rounded-full border py-2 text-sm font-semibold transition ${
                      addressType === value
                        ? "border-primary bg-primary text-secondary"
                        : "border-gray-200 dark:border-[#313131] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#313131]"
                    }`}
                  >
                    {t.header[labelKey]}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleCancel}
                className="flex-1 rounded-lg border border-gray-200 dark:border-[#313131] py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-[#313131] transition"
              >
                {t.common.cancel}
              </button>
              <button
                onClick={handleSave}
                disabled={!pickedResult || createAddress.isPending || updateAddress.isPending || checkingZone}
                className="flex-1 rounded-lg bg-primary py-2.5 text-sm font-semibold text-secondary hover:bg-primary/90 transition disabled:opacity-50"
              >
                {(createAddress.isPending || updateAddress.isPending) ? t.common.saving : t.common.save}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── STEP: adjust-pin ── */}
      {addressStep === "adjust-pin" && (
        <>
          <div className="px-4 pt-4 pb-3">
            <p className="text-base font-semibold text-gray-900 dark:text-white">{t.header.adjustPin}</p>
          </div>

          {/* Drag the map — the centre pin is the drop-off point. */}
          <div ref={dragMap.containerRef} className="relative h-80 bg-[#e8ecef] overflow-hidden">
            {pickedResult && mapStyle ? (
              <MapPreview
                trackResize={false} /* observateur interne : cf. useMapAutoResize */
                initialViewState={{ latitude: pickedResult.latitude, longitude: pickedResult.longitude, zoom: 16 }}
                style={{ width: "100%", height: "100%" }}
                mapStyle={mapStyle}
                attributionControl={false}
                maxBounds={MAP_MAX_BOUNDS}
                onMove={(e) => setPinCoords({ latitude: e.viewState.latitude, longitude: e.viewState.longitude })}
                onLoad={dragMap.onLoad}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <MapPin className="h-10 w-10 text-gray-400" />
              </div>
            )}
            {/* Fixed centre pin */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="flex h-10 w-10 -translate-y-3 items-center justify-center rounded-full bg-gray-900 shadow-xl ring-2 ring-white">
                <MapPin className="h-5 w-5 text-white" />
              </div>
            </div>
          </div>

          <div className="flex gap-3 px-4 py-4">
            <button
              onClick={() => { setPinCoords(null); setAddressStep("detail"); }}
              className="flex-1 rounded-lg border border-gray-200 dark:border-[#313131] py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-[#313131] transition"
            >
              {t.common.cancel}
            </button>
            <button
              onClick={() => {
                // Commit the dragged position as the address coordinates.
                if (pinCoords && pickedResult) {
                  setPickedResult({ ...pickedResult, latitude: pinCoords.latitude, longitude: pinCoords.longitude });
                }
                setPinCoords(null);
                setAddressStep("detail");
              }}
              className="flex-1 rounded-lg bg-primary py-2.5 text-sm font-semibold text-secondary hover:bg-primary/90 transition"
            >
              {t.common.save}
            </button>
          </div>
        </>
      )}
    </>
  );
}
